/**
 * Corner shortcuts: Leitner, Telegram, and AI chat.
 * A toggle above them stacks the three buttons, then slides them to the screen edge.
 */

import React, { useEffect, useRef, useState } from 'react';
import { ChevronsLeft, ChevronsRight, Loader2, MessageCircle, Send, SquareStack, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAppLocale } from '../../providers/LocaleProvider';
import { learnerCornerFabAnchor } from '../../utils/audioBarChromeState';
import { ChatBot } from '../ChatBot';
import { LEITNER_OPEN_EVENT, LeitnerBoxPanel } from '../leitner/LeitnerBoxPanel';
import { LEITNER_CHANGED_EVENT, leitnerService } from '../../services/leitnerService';
import {
  KeepLearningChoiceModal,
  type KeepLearningChoiceModalHandle,
} from './KeepLearningChoiceModal';
import type { LearningResumeEntry } from '../../utils/lastLearningPosition';
import { authService } from '../../services/authService';
import { twoFactorService } from '../../services/twoFactorService';
import { openChannelBotDeepLink } from '../../utils/openChannelBotDeepLink';
import { requestTelegramPhoneHint } from '../../utils/telegramPhoneHint';
import { TOUR_TARGET_FOCUS_EVENT, type TourTargetEventDetail } from '../../learnerTour/tourTargetEvents';
import { prefersReducedMotion } from '../../utils/subscriptionAccessibility';
import { useLearnerHomeDesign } from '../../utils/learnerHomeDesign';

export const LEARNER_OPEN_CHAT_EVENT = 'app:openAiChat';
export const LEARNER_OPEN_TELEGRAM_EVENT = 'app:openTelegramEdge';

const ACTION_COUNT = 3;
const STACK_MS = 320;
const SLIDE_MS = 270;
const EASE = 'cubic-bezier(0.4, 0, 0.2, 1)';
const COLLAPSED_KEY = 'learnerEdgeActionsCollapsed';

type StackPhase = 'expanded' | 'merging' | 'collapsed' | 'arriving' | 'splitting';

const TOUR_EXPAND_KEYS = new Set([
  'header-chatbot',
  'header-leitner',
  'edge-telegram',
  'edge-actions-close',
]);
const TOUR_COLLAPSE_KEY = 'edge-actions-open';

interface LearnerEdgeActionsProps {
  isMobile: boolean;
  audioBarPinned: boolean;
  audioBarExtraChromePx: number;
  bannerCollapsed: boolean;
  keepLearningOpen: boolean;
  keepLearningEntries: LearningResumeEntry[];
  keepLearningModalRef: React.Ref<KeepLearningChoiceModalHandle>;
  onKeepLearningClose: () => void;
  onKeepLearningAfterClose: () => void;
  onKeepLearningSelect: (entry: LearningResumeEntry, anchorEl?: HTMLElement | null) => void;
}

function readPhase(): StackPhase {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === '1' ? 'collapsed' : 'expanded';
  } catch {
    return 'expanded';
  }
}

function DueBadge({ count, className = 'absolute -right-1 -top-1' }: { count: number; className?: string }) {
  if (count <= 0) return null;
  return (
    <span className={`${className} min-w-[1.1rem] rounded-full bg-red-500 px-1 text-center text-[10px] font-bold leading-4 text-white`}>
      {count > 99 ? '99+' : count}
    </span>
  );
}

export const LearnerEdgeActions: React.FC<LearnerEdgeActionsProps> = ({
  isMobile,
  audioBarPinned,
  audioBarExtraChromePx,
  bannerCollapsed,
  keepLearningOpen,
  keepLearningEntries,
  keepLearningModalRef,
  onKeepLearningClose,
  onKeepLearningAfterClose,
  onKeepLearningSelect,
}) => {
  const { t } = useTranslation('common');
  const { t: tLeitner } = useTranslation('leitner');
  const { direction } = useAppLocale();
  const isRtl = direction === 'rtl';
  const [phase, setPhase] = useState<StackPhase>(readPhase);
  const [chatOpen, setChatOpen] = useState(false);
  const [leitnerOpen, setLeitnerOpen] = useState(false);
  const [dueCount, setDueCount] = useState(0);
  const [addedFlash, setAddedFlash] = useState(false);
  const addedFlashTimer = useRef<number | null>(null);
  const [focusCardId, setFocusCardId] = useState<number | undefined>();
  const [focusQuery, setFocusQuery] = useState<string | undefined>();
  const [telegramBusy, setTelegramBusy] = useState(false);

  const anchorOpts = {
    isMobile,
    isRtl,
    bannerCollapsed,
    audioBarPinned,
    audioBarExtraChromePx,
  };

  const panelAnchor = learnerCornerFabAnchor({ ...anchorOpts, stackIndex: 0 });
  const baseBottom = panelAnchor.bottom ?? '1rem';
  const sideKey = isRtl ? 'left' : 'right';
  const stepRem = isMobile ? 2.7 : 3.6;
  const fabSize = isMobile ? 'h-9 w-9' : 'h-11 w-11';
  const iconSize = isMobile ? 'h-4 w-4' : 'h-5 w-5';
  const edgeInset = isMobile ? '0.35rem' : '1rem';
  /** Mobile collapsed tab is 2.75rem wide; half of it sits past the screen edge. */
  const collapsedToggleInset = isMobile ? '-1.375rem' : '0.4rem';
  const interactive = phase === 'expanded';

  useEffect(() => {
    if (phase !== 'expanded' && phase !== 'collapsed') return;
    try {
      localStorage.setItem(COLLAPSED_KEY, phase === 'collapsed' ? '1' : '0');
    } catch {
      /* ignore */
    }
  }, [phase]);

  useEffect(() => {
    if (phase !== 'merging') return undefined;
    const id = window.setTimeout(() => setPhase('collapsed'), STACK_MS + 40);
    return () => window.clearTimeout(id);
  }, [phase]);

  useEffect(() => {
    if (phase !== 'arriving') return undefined;
    const id = window.setTimeout(() => setPhase('splitting'), SLIDE_MS + 30);
    return () => window.clearTimeout(id);
  }, [phase]);

  useEffect(() => {
    if (phase !== 'splitting') return undefined;
    const id = window.setTimeout(() => setPhase('expanded'), STACK_MS + 40);
    return () => window.clearTimeout(id);
  }, [phase]);

  const reveal = () => {
    setPhase((current) => {
      if (current === 'expanded' || current === 'splitting' || current === 'arriving') return current;
      return prefersReducedMotion() ? 'expanded' : 'arriving';
    });
  };

  useEffect(() => {
    const onOpen = (ev: Event) => {
      const e = ev as CustomEvent<{ cardId?: number; highlightTerm?: string }>;
      setFocusCardId(e.detail?.cardId);
      setFocusQuery(e.detail?.highlightTerm);
      reveal();
      setChatOpen(false);
      onKeepLearningClose();
      setLeitnerOpen(true);
    };
    window.addEventListener(LEITNER_OPEN_EVENT, onOpen);
    return () => window.removeEventListener(LEITNER_OPEN_EVENT, onOpen);
  }, [onKeepLearningClose]);

  useEffect(() => {
    const refreshCount = () => {
      void leitnerService.getSummary().then((s) => setDueCount(s.dueCount)).catch(() => undefined);
    };
    const onChanged = (event: Event) => {
      const added = (event as CustomEvent<{ added?: boolean }>).detail?.added === true;
      if (added) {
        setAddedFlash(true);
        if (addedFlashTimer.current != null) window.clearTimeout(addedFlashTimer.current);
        addedFlashTimer.current = window.setTimeout(() => setAddedFlash(false), 5000);
      }
      refreshCount();
    };
    refreshCount();
    window.addEventListener(LEITNER_CHANGED_EVENT, onChanged);
    return () => {
      window.removeEventListener(LEITNER_CHANGED_EVENT, onChanged);
      if (addedFlashTimer.current != null) window.clearTimeout(addedFlashTimer.current);
    };
  }, []);

  useEffect(() => {
    void leitnerService.getSummary().then((s) => setDueCount(s.dueCount)).catch(() => undefined);
  }, [leitnerOpen]);

  const toggleButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!keepLearningOpen) return;
    setChatOpen(false);
    setLeitnerOpen(false);
  }, [keepLearningOpen]);

  const slotStyle = (index: number): React.CSSProperties => {
    const expandedBottom = `calc(${baseBottom} + ${index * stepRem}rem)`;
    const stacked = phase === 'merging' || phase === 'collapsed' || phase === 'arriving';
    const offscreen = phase === 'collapsed';
    const travelMs = phase === 'collapsed' || phase === 'arriving' ? SLIDE_MS : STACK_MS;
    const opacityDelay = phase === 'collapsed' ? Math.max(0, SLIDE_MS - 160) : 0;

    return {
      bottom: stacked ? baseBottom : expandedBottom,
      [sideKey]: offscreen ? '-3.5rem' : edgeInset,
      opacity: offscreen ? 0 : 1,
      pointerEvents: interactive ? 'auto' : 'none',
      zIndex: 40 + index,
      transition: `bottom ${travelMs}ms ${EASE}, ${sideKey} ${travelMs}ms ${EASE}, opacity 180ms ease ${opacityDelay}ms`,
    };
  };

  const toggleAtTop = phase === 'expanded' || phase === 'splitting';
  const toggleMoveMs = phase === 'merging' || phase === 'splitting' ? STACK_MS : SLIDE_MS;
  const toggleStyle: React.CSSProperties = {
    bottom: toggleAtTop ? `calc(${baseBottom} + ${ACTION_COUNT * stepRem}rem)` : baseBottom,
    [sideKey]: phase === 'collapsed' ? collapsedToggleInset : edgeInset,
    transition: `bottom ${toggleMoveMs}ms ${EASE}, ${sideKey} ${toggleMoveMs}ms ${EASE}`,
    zIndex: 40 + ACTION_COUNT,
  };

  const hideStack = () => {
    setChatOpen(false);
    setLeitnerOpen(false);
    onKeepLearningClose();
    setPhase((current) => {
      if (current === 'collapsed' || current === 'merging') return current;
      if (prefersReducedMotion() || current === 'arriving') return 'collapsed';
      return 'merging';
    });
  };

  useEffect(() => {
    const onFocus = (event: Event) => {
      const key = (event as CustomEvent<TourTargetEventDetail>).detail?.targetKey;
      const button = toggleButtonRef.current;
      if (key && TOUR_EXPAND_KEYS.has(key)) {
        reveal();
        if (key === 'edge-actions-close') button?.setAttribute('data-tour-id', 'edge-actions-close');
      } else if (key === TOUR_COLLAPSE_KEY) {
        hideStack();
        button?.setAttribute('data-tour-id', 'edge-actions-open');
      }
    };
    window.addEventListener(TOUR_TARGET_FOCUS_EVENT, onFocus);
    return () => window.removeEventListener(TOUR_TARGET_FOCUS_EVENT, onFocus);
  }, []);

  const [homeDesign] = useLearnerHomeDesign();
  const simpleHome = homeDesign === 'mme';
  const showChevron = phase === 'collapsed';
  const ChevronIcon = isRtl ? ChevronsRight : ChevronsLeft;
  const iconMotion: React.CSSProperties = {
    transition: `opacity ${SLIDE_MS}ms ease, transform ${SLIDE_MS}ms ${EASE}`,
  };

  const openChat = () => {
    setLeitnerOpen(false);
    onKeepLearningClose();
    setChatOpen(true);
  };

  const openLeitner = () => {
    setFocusCardId(undefined);
    setFocusQuery(undefined);
    setChatOpen(false);
    onKeepLearningClose();
    setLeitnerOpen(true);
  };

  const openTelegram = async () => {
    if (telegramBusy) return;
    setTelegramBusy(true);
    try {
      let profile = authService.getCurrentUser();
      let phone = profile?.phoneNumber?.trim() ?? '';
      let confirmed = Boolean(profile?.phoneNumberConfirmed);
      if (!phone || !confirmed) {
        try {
          profile = await authService.getProfile(true);
          phone = profile.phoneNumber?.trim() ?? '';
          confirmed = Boolean(profile.phoneNumberConfirmed);
        } catch {
          /* use the cached profile */
        }
      }
      if (phone && confirmed) {
        let username = '';
        try {
          const status = await twoFactorService.getStatus();
          username = (status.telegramBotUsername ?? '').replace(/^@/, '');
        } catch {
          username = '';
        }
        if (!username) {
          try {
            const link = await twoFactorService.getTelegramLink();
            const raw = link as unknown as Record<string, unknown>;
            username = String(raw.botUsername ?? raw.BotUsername ?? '').replace(/^@/, '');
          } catch {
            username = '';
          }
        }
        if (username) {
          await openChannelBotDeepLink(`https://t.me/${username}`, 'telegram');
          return;
        }
      }
      window.dispatchEvent(new CustomEvent('app:navigate', { detail: { view: 'profile', hash: 'profile' } }));
      requestTelegramPhoneHint();
    } finally {
      setTelegramBusy(false);
    }
  };

  useEffect(() => {
    const onChat = () => openChat();
    const onTelegram = () => {
      void openTelegram();
    };
    window.addEventListener(LEARNER_OPEN_CHAT_EVENT, onChat);
    window.addEventListener(LEARNER_OPEN_TELEGRAM_EVENT, onTelegram);
    return () => {
      window.removeEventListener(LEARNER_OPEN_CHAT_EVENT, onChat);
      window.removeEventListener(LEARNER_OPEN_TELEGRAM_EVENT, onTelegram);
    };
  }, [telegramBusy, onKeepLearningClose]);

  const fabButton = `edge-fab relative flex ${fabSize} items-center justify-center rounded-full shadow-lg`;
  const flashLeitner = addedFlash && phase !== 'collapsed';
  const flashShortcut = addedFlash && phase === 'collapsed';
  const mobilePeek = isMobile && showChevron;
  const toggleButtonClass = mobilePeek
    ? `edge-fab edge-shortcut-toggle relative flex h-9 w-11 items-center shadow-lg ${
        isRtl ? 'justify-end rounded-l-none rounded-r-full pr-1' : 'justify-start rounded-l-full rounded-r-none pl-1'
      } ${flashShortcut ? 'search-result-highlight' : ''}`
    : `${fabButton} edge-shortcut-toggle ${flashShortcut ? 'search-result-highlight' : ''}`;

  return (
    <>
      {simpleHome ? null : (
      <>
      <div className="edge-fab-slot fixed" style={toggleStyle}>
        <button
          ref={toggleButtonRef}
          type="button"
          data-tour-id={showChevron ? 'edge-actions-open' : 'edge-actions-close'}
          onClick={() => (phase === 'collapsed' ? reveal() : hideStack())}
          dir={mobilePeek ? 'ltr' : undefined}
          className={toggleButtonClass}
          aria-label={showChevron ? t('edgeActions.open') : t('edgeActions.close')}
          title={showChevron ? t('edgeActions.open') : t('edgeActions.close')}
          aria-expanded={phase !== 'collapsed'}
        >
          <span className={`relative grid place-items-center ${iconSize}`}>
            <X
              className={`col-start-1 row-start-1 text-black ${iconSize} ${showChevron ? 'rotate-90 scale-50 opacity-0' : 'rotate-0 scale-100 opacity-100'}`}
              style={iconMotion}
            />
            <ChevronIcon
              className={`col-start-1 row-start-1 ${iconSize} ${showChevron ? 'rotate-0 scale-100 opacity-100' : '-rotate-90 scale-50 opacity-0'}`}
              style={iconMotion}
            />
          </span>
          {showChevron ? (
            <DueBadge
              count={dueCount}
              className={
                mobilePeek
                  ? `absolute -top-1 ${isRtl ? 'right-0.5' : 'left-0.5'}`
                  : 'absolute -right-1 -top-1'
              }
            />
          ) : null}
        </button>
      </div>

      <div className="edge-fab-slot fixed" style={slotStyle(2)} aria-hidden={!interactive}>
        <button
          type="button"
          data-tour-id="header-leitner"
          onClick={openLeitner}
          className={`${fabButton} ${flashLeitner ? 'search-result-highlight' : ''}`}
          aria-label={tLeitner('open')}
          title={tLeitner('title')}
          tabIndex={interactive ? 0 : -1}
        >
          <SquareStack className={iconSize} />
          {phase === 'collapsed' ? null : <DueBadge count={dueCount} />}
        </button>
      </div>

      <div className="edge-fab-slot fixed" style={slotStyle(1)} aria-hidden={!interactive}>
        <button
          type="button"
          data-tour-id="edge-telegram"
          onClick={() => void openTelegram()}
          disabled={telegramBusy}
          className={`${fabButton} disabled:opacity-70`}
          aria-label={t('edgeActions.telegram')}
          title={telegramBusy ? t('edgeActions.telegramOpening') : t('edgeActions.telegram')}
          aria-busy={telegramBusy}
          tabIndex={interactive ? 0 : -1}
        >
          {telegramBusy ? <Loader2 className={`${iconSize} animate-spin`} /> : <Send className={iconSize} />}
        </button>
      </div>

      <div className="edge-fab-slot fixed" style={slotStyle(0)} aria-hidden={!interactive}>
        <button
          type="button"
          data-tour-id="header-chatbot"
          onClick={openChat}
          className={fabButton}
          aria-label={t('openAiChat')}
          title={t('aiChat')}
          tabIndex={interactive ? 0 : -1}
        >
          <MessageCircle className={iconSize} />
        </button>
      </div>
      </>
      )}

      <ChatBot isOpen={chatOpen} onClose={() => setChatOpen(false)} anchorStyle={panelAnchor} />
      <LeitnerBoxPanel
        isOpen={leitnerOpen}
        onClose={() => setLeitnerOpen(false)}
        anchorStyle={panelAnchor}
        initialCardId={focusCardId}
        initialQuery={focusQuery}
      />
      <KeepLearningChoiceModal
        ref={keepLearningModalRef}
        open={keepLearningOpen}
        entries={keepLearningEntries}
        onClose={onKeepLearningClose}
        onAfterClose={onKeepLearningAfterClose}
        onSelect={onKeepLearningSelect}
      />
    </>
  );
};
